import { Typography } from '@material-ui/core';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import Grid from '@material-ui/core/Grid';
import Paper from '@material-ui/core/Paper';
import React, { FunctionComponent, useEffect } from 'react';

import { useTranslation } from '../../../../hooks/useTranslation';
import ROUTES from '../../../../routes';
import GuestPaymentHeaderSection from './guest-payment-header-section';
import GuestPaymentInfoSection from './guest-payment-info-section';
import { FormState } from '../../../../hooks/useFormState.types';
import { Model } from './GuestPaymentForm.rules';
import { GuestPaymentAccount } from '../GuestPaymentTypes';
import { navigate } from '@reach/router';
import useStyles from './GuestPaymentForm.styles';
import { PageTextContentType } from '../../../../hooks/useContentMgmt';
import PGEButton from '../../../buttons/PGE-Button';
import { PaymentProfileInfo } from '../../../paymentus/types';
import { PaymentCategory } from '../../../../__generated__/pge-types';
import AddPaymentMethodDropdown from '../../../payment-method-type/AddPaymentMethodDropdown';
import PaymentMethodTypeIconList from '../../../payment-method-type/PaymentMethodTypeIconList';
import { getAccountDisplayString } from '../../../paymentus/utils';
import {
  getPaymentCategoryLabel,
  getPaymentMethodLabel,
} from '../../../../hooks/usePaymentus';

interface Props {
  path?: string;
  guestPaymentForm: FormState<Model>;
  guestPaymentAccount: GuestPaymentAccount;
  handleOnSaveProfile: (profile: PaymentProfileInfo) => void;
  handleOnGuestPayCancel: () => void;
  handleOnGuestPayNext: () => void;
  paymentDate: string;
  content: PageTextContentType;
}

const GuestPaymentForm: FunctionComponent<Props> = props => {
  const { t } = useTranslation();
  const classes = useStyles();
  const {
    guestPaymentForm,
    guestPaymentAccount,
    handleOnGuestPayNext,
    handleOnGuestPayCancel,
    handleOnSaveProfile,
    paymentDate,
  } = props;
  const addOrUpdateProps = {
    customLabel: t('ENTER_A_PAYMENT_METHOD'),
    currentProfileInfo: guestPaymentForm.values.paymentProfile,
    isPrimaryPM: false,
    onChange: (profile: PaymentProfileInfo) => {
      handleOnSaveProfile(profile);
    },
    onNew: (profile: PaymentProfileInfo) => {
      handleOnSaveProfile(profile);
    },
    allowedCategories: [
      PaymentCategory.Dd,
      PaymentCategory.Cc,
      PaymentCategory.Dc,
      PaymentCategory.PaypalAccount,
    ],
  };
  const escapedMaskedBankAccountNumber = getAccountDisplayString(
    guestPaymentForm.values.paymentProfile?.profile,
  );
  const paymentAmountDisplay = `$${guestPaymentForm.values.amount}`;
  const selectedPaymentCategory =
    guestPaymentForm.values.paymentProfile?.pmCategory;
  const paymentMethodToDisplay =
    selectedPaymentCategory === 'DD'
      ? t('BANK_ACCOUNT_NUMBER')
      : selectedPaymentCategory === 'CC'
      ? t('CREDIT_CARD_NUMBER')
      : selectedPaymentCategory === 'DC'
      ? t('DEBIT_CARD_NUMBER')
      : getPaymentMethodLabel(
          guestPaymentForm.values.paymentProfile?.profile?.type,
        );

  const paymentCategoryToDisplay = getPaymentCategoryLabel(
    guestPaymentForm.values.paymentProfile?.profile?.type,
    true,
  );

  useEffect(() => {
    if (guestPaymentAccount.encryptedPersonId.length <= 0) {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      navigate(ROUTES.INVOICE_PAYMENT_GUEST_PAY);
    }
  }, []);
  const labelComponent = (
    <Typography style={{ float: 'left' }}> + {t('WE_ACCEPT')}</Typography>
  );
  return (
    <Grid container spacing={1} className={classes.mainContainer}>
      <div style={{ padding: '.25em' }} />
      <GuestPaymentHeaderSection
        accountNumber={guestPaymentAccount.invoiceId}
        partialAddress={guestPaymentAccount.partialAddressLineOne}
        projectNum={guestPaymentAccount.projectNumber}
        content={props?.content}
        encryptedBillId={guestPaymentAccount.encryptedBillId!}
      />
      <div style={{ padding: '.75em' }} />
      <Paper>
        <Card>
          <CardContent>
            <GuestPaymentInfoSection
              form={guestPaymentForm}
              paymentDate={paymentDate}
              dueDate={guestPaymentAccount?.dueDate}
              amountDue={guestPaymentAccount?.amountDue}
            />

            {guestPaymentAccount?.amountDue &&
              Number(guestPaymentAccount?.amountDue) > 0 && (
                <>
                  <div style={{ padding: '.75em' }} />
                  <AddPaymentMethodDropdown {...addOrUpdateProps} />
                  <PaymentMethodTypeIconList labelComponent={labelComponent} />
                  {guestPaymentForm?.values?.paymentProfile && (
                    <Grid
                      container
                      direction="row"
                      spacing={1}
                      style={{ marginTop: '2em' }}
                    >
                      <Grid item xs={6} style={{ marginBottom: '.75em' }}>
                        <Grid item xs={12}>
                          <Typography
                            className={classes.colorTextPrimary}
                            variant={'body1'}
                          >
                            {paymentMethodToDisplay}
                          </Typography>
                          <Typography
                            className={classes.colorTextPrimary}
                            variant={'subtitle2'}
                          >
                            {escapedMaskedBankAccountNumber ||
                              `${t('GUEST_PAY_AUTHORIZED')}`}
                          </Typography>
                        </Grid>
                      </Grid>
                      <Grid item xs={6} style={{ marginBottom: '.75em' }}>
                        <Grid item xs={12}>
                          <Typography
                            className={classes.colorTextPrimary}
                            variant={'body1'}
                          >
                            {t('PAYMENT_CONFIRMATION_METHOD')}
                          </Typography>
                          <Typography
                            className={classes.colorTextPrimary}
                            variant={'subtitle2'}
                          >
                            {paymentMethodToDisplay}
                          </Typography>
                        </Grid>
                      </Grid>
                      <Grid item xs={12}></Grid>
                    </Grid>
                  )}
                </>
              )}
          </CardContent>
        </Card>
      </Paper>
      {guestPaymentAccount?.amountDue &&
        Number(guestPaymentAccount?.amountDue) > 0 && (
          <Grid item xs={12} className={classes.buttonContainer}>
            <PGEButton
              type={'button'}
              variant="contained"
              color="primary"
              disabled={!guestPaymentForm?.values?.paymentProfile}
              size="large"
              css={{
                borderRadius: '40px',
                textAlign: 'right',
              }}
              onClick={handleOnGuestPayNext}
            >
              {t('NEXT')}
            </PGEButton>
          </Grid>
        )}
    </Grid>
  );
};

export default GuestPaymentForm;
